import {
  Box,
  Container,
  Paper,
  useTheme,
  useMediaQuery,
  Typography,
} from "@mui/material";
import backgroundImage from "@/assets/background.webp";
import LanguageSelect from "@/components/LanguageSelector";
import { useTranslation } from "react-i18next";

interface AuthWrapperProps {
  children: React.ReactNode;
  isOnboarding?: boolean;
}

const AuthWrapper = ({ children, isOnboarding = false }: AuthWrapperProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${theme.palette.primary.light}20 0%, ${theme.palette.primary.main}10 100%)`,
        padding: 2,
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: { xs: 12, md: 20 },
          right: { xs: 12, md: 24 },
          zIndex: 10,
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(255, 255, 255, 0.85)",
          borderRadius: 2,
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
          border: "1px solid",
          borderColor: "divider",
          px: 0.5,
          py: 0.25,
          transition: "box-shadow 0.2s ease",
          "&:hover": {
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
          },
        }}
      >
        <LanguageSelect />
      </Box>

      <Container maxWidth="lg">
        <Paper
          elevation={8}
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            borderRadius: 3,
            overflow: "hidden",
            minHeight: isMobile ? "auto" : "600px",
            maxWidth: "1000px",
            margin: "0 auto",
            backgroundColor: "background.paper",
          }}
        >
          {/* Form Section */}
          <Box
            sx={{
              flex: isMobile ? "none" : "1 1 50%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              padding: isMobile ? 3 : 5,
              minHeight: isMobile ? "400px" : "600px",
              order: isMobile ? 2 : 1,
            }}
          >
            {children}
          </Box>

          {/* Image Section */}
          <Box
            sx={{
              flex: isMobile ? "none" : "1 1 50%",
              position: "relative",
              minHeight: isMobile ? "200px" : "600px",
              order: isMobile ? 1 : 2,
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={backgroundImage}
              alt="Background"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            />
            {/* Overlay for better text readability */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(45deg, rgba(100, 120, 140, 0.95), rgba(100, 120, 140, 0.4))`,
              }}
            />
            {/* Text Overlay */}
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                color: "white",
                zIndex: 1,
                width: "90%",
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
                  fontSize: { xs: "1.75rem", md: "2.5rem" },
                }}
              >
                {t("wrapper_title")}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 400,
                  textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
                  opacity: 0.95,
                  fontSize: { xs: "1rem", md: "1.25rem" },
                }}
              >
                {t("wrapper_subtitle")}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AuthWrapper;
